import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Copy, Globe, EyeOff, HelpCircle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

// Customer-facing production domain for all copy-paste embed snippets & links (so what the
// seller pastes into Shopify always points at the real storefront-facing domain, regardless of
// which URL they happen to be viewing this dashboard from — e.g. an internal preview URL).
// Falls back to the current origin if the env var isn't set.
const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_CUSTOMER_URL || window.location.origin;

export default function Dashboard() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("stamp");
  const [showEmbedHelp, setShowEmbedHelp] = useState(false);
  const [testUrl, setTestUrl] = useState("");
  const [testSku, setTestSku] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch { toast.error("Failed to load templates"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      // Base payload
      const payload = { name: newName, category: newCategory, zones: [] };
      if (newCategory === "face_stamp") {
        // Scaffold: face image on top (75%), text below (20%), sensible ink slot + limits
        const rand = () => Math.random().toString(36).slice(2, 8);
        const faceZoneId = `z-face-${rand()}`;
        const textZoneId = `z-text-${rand()}`;
        const inkSlotId = `slot-${rand()}`;
        payload.views = [{
          id: `view-front-${rand()}`,
          name: "Front",
          base_image_url: "",
          bg_color: "#ffffff",
          zones: [
            {
              id: faceZoneId,
              type: "image",
              x: 125, y: 40, width: 750, height: 750,
              label: "Face photo",
              customer_editable: true,
              allow_upload: true,
              min_image_dimension: 300,
              default_image_url: "",
            },
            {
              id: textZoneId,
              type: "text",
              x: 60, y: 800, width: 880, height: 170,
              label: "Custom text",
              default_text: "Your name here",
              default_font: "Bebas Neue",
              default_color: "#111111",
              min_font_size: 24, max_font_size: 220,
              customer_editable: true,
              allow_font_change: true,
              allow_color_change: true,
              allow_curve: false,
              ink_slot_id: inkSlotId,
            },
          ],
        }];
        payload.ink_slots = [{
          id: inkSlotId,
          label: "Ink color",
          colors: ["#111111", "#1e3a8a", "#7f1d1d"],
          default_color: "#111111",
        }];
        payload.max_text_limit = 30;
        payload.whatsapp_required = true;
        payload.notice = "Upload a clear front-facing photo — we'll convert it to a black-and-white sketch instantly.";
        payload.description = "Face Stamp — photo sketch + custom text";
      }
      const { data } = await api.post("/templates", payload);
      toast.success(newCategory === "face_stamp" ? "Face Stamp scaffold created" : "Template created");
      setNewName(""); setNewCategory("stamp"); setCreating(false);
      window.location.href = `/templates/${data.id}/edit`;
    } catch { toast.error("Create failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  const copyToClipboard = (text, successMsg) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success(successMsg);
        else toast.error("Couldn't copy automatically — please select and copy the snippet manually.");
      } catch {
        toast.error("Couldn't copy automatically — please select and copy the snippet manually.");
      }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(successMsg)).catch(fallback);
    } else {
      fallback();
    }
  };

  const copyEmbed = (t) => {
    const base = PUBLIC_BASE;
    // Full-width block button — stacks vertically with Shopify's "Add to Cart" / "Buy it now" buttons, never overlaps.
    // Inherits page font, uses white bg + black text (safe on any theme), swaps on hover.
    const snippet = `<a href="${base}/c/${t.id}" target="_blank" rel="noopener" style="display:block;width:100%;box-sizing:border-box;padding:14px 22px;margin:8px 0;background:#ffffff;color:#000000;border:1px solid #000000;border-radius:6px;font-weight:600;font-size:16px;text-decoration:none;text-align:center;line-height:1.2;font-family:inherit;cursor:pointer;transition:background .15s ease,color .15s ease;" onmouseover="this.style.background='#000';this.style.color='#fff';" onmouseout="this.style.background='#fff';this.style.color='#000';">Customise now</a>`;
    copyToClipboard(snippet, "Copied! Paste in a Shopify Custom Liquid block under 'Add to cart' — full-width, black-on-white.");
  };

  const copyIframe = (t) => {
    const base = PUBLIC_BASE;
    const snippet = `<iframe src="${base}/c/${t.id}" width="100%" height="900" style="border:0;max-width:100%;display:block;" title="Product customizer"></iframe>`;
    copyToClipboard(snippet, "iframe snippet copied!");
  };

  const copyInline = (t) => {
    const base = PUBLIC_BASE;
    // Compact inline variant for small placements (e.g. beside a title).
    const snippet = `<a href="${base}/c/${t.id}" target="_blank" rel="noopener" style="display:inline-block;padding:8px 16px;background:#ffffff;color:#000000;border:1px solid #000000;border-radius:6px;font-weight:600;font-size:14px;text-decoration:none;font-family:inherit;">Customise now</a>`;
    copyToClipboard(snippet, "Inline compact snippet copied.");
  };

  const copyLink = (t) => {
    copyToClipboard(`${PUBLIC_BASE}/c/${t.id}`, "Direct link copied!");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-600">Your studio</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl">Templates</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowEmbedHelp((v) => !v)} className="brutal-btn bg-white px-4 py-3 rounded-md flex items-center gap-2 text-sm" data-testid="embed-help-toggle">
              <HelpCircle className="w-4 h-4" /> How to embed on Shopify
            </button>
            <button onClick={() => setCreating(true)} className="brutal-btn bg-yellow-300 px-5 py-3 rounded-md flex items-center gap-2" data-testid="new-template-btn">
              <Plus className="w-4 h-4" /> New template
            </button>
          </div>
        </div>

        {showEmbedHelp && (
          <div className="brutal-card p-5 mb-6 bg-blue-50" data-testid="embed-help">
            {/* Diagnostic — Test my mapping */}
            <div className="brutal-card p-4 mb-5 bg-white">
              <div className="font-heading font-black text-base mb-1">Test my mapping</div>
              <p className="text-sm text-stone-800 mb-3">
                Paste your Shopify product URL or SKU. This checks the exact same lookup the smart embed uses on your storefront.
              </p>
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <input className="brutal-input text-xs" placeholder="Shopify product URL" value={testUrl} onChange={(e) => setTestUrl(e.target.value)} data-testid="test-url-input"/>
                <input className="brutal-input text-xs" placeholder="Or Shopify product SKU" value={testSku} onChange={(e) => setTestSku(e.target.value)} data-testid="test-sku-input"/>
              </div>
              <button onClick={async () => {
                if (!testUrl.trim() && !testSku.trim()) { toast.error("Enter a URL or SKU"); return; }
                setTesting(true); setTestResult(null);
                try {
                  const params = new URLSearchParams();
                  if (testSku.trim()) params.set("sku", testSku.trim());
                  if (testUrl.trim()) params.set("url", testUrl.trim());
                  const { data } = await axios.get(`${API}/public/templates/lookup?${params.toString()}`);
                  setTestResult(data);
                } catch (e) {
                  setTestResult({ error: e.response?.data?.detail || "Lookup failed" });
                }
                setTesting(false);
              }} className="brutal-btn bg-yellow-300 px-4 py-2 rounded-md text-sm" data-testid="run-mapping-test">
                {testing ? <><Loader2 className="w-4 h-4 inline mr-1 animate-spin"/> Testing…</> : <>Test mapping</>}
              </button>

              {testResult && (
                <div className="mt-3" data-testid="test-result">
                  {testResult.error ? (
                    <div className="brutal-card bg-red-50 p-3 border-red-500 text-sm text-red-800">
                      <XCircle className="w-4 h-4 inline mr-1"/> {testResult.error}
                    </div>
                  ) : testResult.template ? (
                    <div className="brutal-card bg-green-50 p-3 border-green-700 text-sm">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5"/>
                        <div className="flex-1">
                          <div className="font-bold text-green-900">Match found — the button will appear.</div>
                          <div className="text-stone-700 mt-1"><b>Template:</b> {testResult.template.name}</div>
                          <div className="text-xs text-stone-600">ID: <code>{testResult.template.id}</code></div>
                          <div className="text-xs text-stone-600">Direct URL: <a href={`${PUBLIC_BASE}/c/${testResult.template.id}`} target="_blank" rel="noreferrer" className="underline">{PUBLIC_BASE}/c/{testResult.template.id}</a></div>
                        </div>
                      </div>
                      <div className="mt-3 border-t border-stone-300 pt-3">
                        <div className="text-xs font-bold text-stone-700 mb-1">Live preview — this is what the smart embed will render on the Shopify page:</div>
                        <iframe title="smart-embed-preview" src={`${API}/embed/button?${new URLSearchParams({ ...(testSku ? {sku:testSku} : {}), ...(testUrl ? {url:testUrl} : {}) }).toString()}`}
                          style={{ width: "100%", minHeight: 70, border: "1px dashed #94a3b8", borderRadius: 6, background: "#fff" }}/>
                      </div>
                    </div>
                  ) : (
                    <div className="brutal-card bg-yellow-50 p-3 border-yellow-700 text-sm">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-5 h-5 text-yellow-800 shrink-0 mt-0.5"/>
                        <div>
                          <div className="font-bold text-yellow-900">No template matches this product.</div>
                          <div className="text-stone-700 mt-1 text-xs">Check that a <b>published</b> template has the exact same <b>Product SKU</b> or <b>Assigned product page URL</b> as what you entered above. URL matching is case-insensitive but must match the path (everything after the domain).</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="font-heading font-black text-lg mb-2">Smart embed — one universal snippet</div>
            <p className="text-sm text-stone-800 mb-3">
              <b>Method A (Liquid snippet)</b> gives the most exact result — Shopify injects the live variant SKU server-side, so the button always matches the SKU currently selected, with no DOM-guessing. <b>Method B (hosted script)</b> is a one-time, zero-Liquid install across every product page.
            </p>

            <div className="mb-5 pb-5 border-b-2 border-stone-900">
              <div className="font-heading font-black text-base mb-1">Method A · Liquid snippet (recommended — exact SKU per variant)</div>
              <p className="text-sm text-stone-800 mb-2">
                Shopify&apos;s Liquid <code>product.variants</code> object is injected directly into the snippet, so the button always resolves the <b>exact SKU of the currently selected variant</b> — no DOM-scraping, no guessing. Re-renders instantly when the shopper switches variants (listens for Shopify&apos;s canonical <code>select[name=&quot;id&quot;]</code> / <code>input[name=&quot;id&quot;]</code> form field).
              </p>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-1">Paste inside the product template, right after Add to cart</div>
              <div className="bg-white border-2 border-stone-900 rounded p-3 font-mono text-xs overflow-x-auto whitespace-pre mb-2" data-testid="liquid-snippet-code">{`<!-- CanvasKit: paste inside {% form 'product', product %}...{% endform %},
     right after the Add to cart button, in sections/main-product.liquid
     (or product-template.liquid on vintage/non-OS2.0 themes) -->
<div id="ck-embed-root"></div>
<script>
(function(){
  var BASE = "${PUBLIC_BASE}";
  var variants = {{ product.variants | json }};
  var root = document.getElementById('ck-embed-root');
  function skuFor(variantId){
    for (var i=0;i<variants.length;i++){ if (String(variants[i].id) === String(variantId)) return variants[i].sku || ""; }
    return "";
  }
  function currentVariantId(){
    var sel = document.querySelector('select[name="id"], input[name="id"]:checked, input[type="hidden"][name="id"]');
    if (sel && sel.value) return sel.value;
    var params = new URLSearchParams(window.location.search);
    return params.get('variant') || "{{ product.selected_or_first_available_variant.id }}";
  }
  function render(){
    var sku = skuFor(currentVariantId());
    root.innerHTML = '';
    if (!sku) return;
    fetch(BASE + "/api/public/templates/lookup?sku=" + encodeURIComponent(sku))
      .then(function(r){ return r.json(); })
      .then(function(d){
        root.innerHTML = '';
        if (!d || !d.template) return;
        var a = document.createElement('a');
        a.href = BASE + '/c/' + d.template.id + '?sku=' + encodeURIComponent(sku);
        a.target = '_top'; a.textContent = 'Customise now';
        a.setAttribute('data-testid','ck-customise-button');
        a.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:14px 22px;margin-top:8px;background:#fff;color:#000;border:1px solid #000;border-radius:6px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;';
        root.appendChild(a);
      }).catch(function(){});
  }
  render();
  document.addEventListener('change', function(e){
    if (e.target && (e.target.matches('select[name="id"]') || e.target.matches('input[name="id"]'))) render();
  });
  var hidden = document.querySelector('input[type="hidden"][name="id"]');
  if (hidden) new MutationObserver(render).observe(hidden, {attributes:true});
})();
</script>`}</div>
              <button onClick={() => {
                const snippet = `<!-- CanvasKit: paste inside {% form 'product', product %}...{% endform %},\n     right after the Add to cart button, in sections/main-product.liquid\n     (or product-template.liquid on vintage/non-OS2.0 themes) -->\n<div id="ck-embed-root"></div>\n<script>\n(function(){\n  var BASE = "${PUBLIC_BASE}";\n  var variants = {{ product.variants | json }};\n  var root = document.getElementById('ck-embed-root');\n  function skuFor(variantId){\n    for (var i=0;i<variants.length;i++){ if (String(variants[i].id) === String(variantId)) return variants[i].sku || ""; }\n    return "";\n  }\n  function currentVariantId(){\n    var sel = document.querySelector('select[name="id"], input[name="id"]:checked, input[type="hidden"][name="id"]');\n    if (sel && sel.value) return sel.value;\n    var params = new URLSearchParams(window.location.search);\n    return params.get('variant') || "{{ product.selected_or_first_available_variant.id }}";\n  }\n  function render(){\n    var sku = skuFor(currentVariantId());\n    root.innerHTML = '';\n    if (!sku) return;\n    fetch(BASE + "/api/public/templates/lookup?sku=" + encodeURIComponent(sku))\n      .then(function(r){ return r.json(); })\n      .then(function(d){\n        root.innerHTML = '';\n        if (!d || !d.template) return;\n        var a = document.createElement('a');\n        a.href = BASE + '/c/' + d.template.id + '?sku=' + encodeURIComponent(sku);\n        a.target = '_top'; a.textContent = 'Customise now';\n        a.setAttribute('data-testid','ck-customise-button');\n        a.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:14px 22px;margin-top:8px;background:#fff;color:#000;border:1px solid #000;border-radius:6px;font-weight:600;text-align:center;text-decoration:none;font-family:inherit;';\n        root.appendChild(a);\n      }).catch(function(){});\n  }\n  render();\n  document.addEventListener('change', function(e){\n    if (e.target && (e.target.matches('select[name="id"]') || e.target.matches('input[name="id"]'))) render();\n  });\n  var hidden = document.querySelector('input[type="hidden"][name="id"]');\n  if (hidden) new MutationObserver(render).observe(hidden, {attributes:true});\n})();\n</script>`;
                copyToClipboard(snippet, "Liquid snippet copied — paste in your product template's theme code.");
              }} className="brutal-btn bg-lime-300 px-4 py-2 rounded-md text-sm mb-3" data-testid="copy-liquid-snippet">
                <Copy className="w-4 h-4 inline mr-1"/> Copy Liquid snippet
              </button>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-1">Steps</div>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-stone-800">
                <li>In the CanvasKit template, fill <b>Product SKU</b> to exactly match the Shopify variant&apos;s SKU. Publish.</li>
                <li>In Shopify Admin: <b>Online Store → Themes → &#8942; → Edit code</b>.</li>
                <li>Open <code>sections/main-product.liquid</code> (or your theme&apos;s product template file) and paste the snippet right after the Add to cart <code>&lt;button&gt;</code>, inside the product form.</li>
                <li>Save. Visit the live product page — the button appears immediately below Add to cart, and follows the SKU whenever the shopper changes variant.</li>
              </ol>
            </div>

            <div className="font-heading font-black text-base mb-1">Method B · Hosted script (universal — one-time install, every product page)</div>
            <p className="text-sm text-stone-800 mb-2">
              Runs in the storefront page itself (not an iframe), so it can locate Shopify&apos;s Add-to-cart button (<code>button[name=&quot;add&quot;]</code> / <code>form[action*=&quot;/cart/add&quot;]</code>) and drop the button right next to it — one script, every product, no per-page edits. Detects the SKU from Shopify&apos;s own auto-generated product JSON-LD, re-checking on variant change.
            </p>
            <div className="bg-white border-2 border-stone-900 rounded p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap break-all mb-2" data-testid="inline-embed-code">{`<script src="${PUBLIC_BASE}/api/embed/inline.js" defer></script>`}</div>
            <button onClick={() => {
              const snippet = `<script src="${PUBLIC_BASE}/api/embed/inline.js" defer></script>`;
              copyToClipboard(snippet, "Hosted script copied — paste in theme.liquid before </body>.");
            }} className="brutal-btn bg-yellow-300 px-4 py-2 rounded-md text-sm mb-3" data-testid="copy-inline-embed">
              <Copy className="w-4 h-4 inline mr-1"/> Copy hosted script
            </button>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-600 mb-1">Steps</div>
            <ol className="list-decimal ml-5 space-y-1 text-sm text-stone-800">
              <li>In Shopify Admin: <b>Online Store → Themes → &#8942; → Edit code → Layout → theme.liquid</b>.</li>
              <li>Paste the snippet above right before <code>&lt;/body&gt;</code>. Save.</li>
              <li>Publish/Save the theme. The button now appears on every product page whose SKU (or page URL) maps to a published CanvasKit template — no further edits needed as you add products.</li>
            </ol>

            <div className="mt-5 pt-5 border-t-2 border-stone-900">
              <div className="font-heading font-black text-base mb-1">Method C · Raw iframe (fallback — appears wherever you drop it)</div>
              <div className="bg-white border-2 border-stone-900 rounded p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap break-all mb-2" data-testid="smart-embed-code">{`<iframe id="ck-customise" src="${PUBLIC_BASE}/api/embed/button?sku=YOUR_PRODUCT_SKU" width="100%" height="0" frameborder="0" scrolling="no" style="border:0;display:block;max-width:100%;overflow:hidden;"></iframe>
<script>window.addEventListener("message",function(e){if(e&&e.data&&e.data.type==="ck-resize"){var f=document.getElementById("ck-customise");if(f)f.style.height=(e.data.height||0)+"px";}});</script>`}</div>
              <button onClick={() => {
                const snippet = `<iframe id="ck-customise" src="${PUBLIC_BASE}/api/embed/button?sku=YOUR_PRODUCT_SKU" width="100%" height="0" frameborder="0" scrolling="no" style="border:0;display:block;max-width:100%;overflow:hidden;"></iframe>\n<script>window.addEventListener("message",function(e){if(e&&e.data&&e.data.type==="ck-resize"){var f=document.getElementById("ck-customise");if(f)f.style.height=(e.data.height||0)+"px";}});</script>`;
                copyToClipboard(snippet, "Raw iframe snippet copied — replace YOUR_PRODUCT_SKU with the real SKU");
              }} className="brutal-btn bg-yellow-300 px-4 py-2 rounded-md text-sm mb-4" data-testid="copy-smart-embed">
                <Copy className="w-4 h-4 inline mr-1"/> Copy raw iframe
              </button>
              <div className="font-heading font-black text-sm mb-1 mt-3">Steps (raw iframe route)</div>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-stone-800">
                <li>In the CanvasKit template, fill the <b>Product SKU</b> to exactly match the Shopify variant&apos;s SKU.</li>
                <li>Publish the template.</li>
                <li>In Shopify theme code, drop a <b>Custom Liquid</b> block below &ldquo;Add to cart&rdquo; &amp; &ldquo;Buy it now&rdquo;, width 100%.</li>
                <li>Paste the raw iframe snippet above and replace <code>YOUR_PRODUCT_SKU</code> with the variant&apos;s SKU. Height auto-adjusts (0px hidden, ~60px shown).</li>
                <li>Save. The button appears only on products whose SKU maps to a published template.</li>
              </ol>
              <div className="text-xs text-stone-600 mt-3">
                Matching is <b>SKU-only</b>. The iframe must receive <code>?sku=&lt;product-sku&gt;</code> in its src. When the SKU matches a published template, the button appears; otherwise the iframe collapses to 0&nbsp;px and nobody sees it.
              </div>
            </div>
          </div>
        )}

        {creating && (
          <form onSubmit={create} className="brutal-card p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="new-template-form">
            <input autoFocus className="brutal-input flex-1" placeholder="Template name (e.g. Custom Stamp)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-template-name" />
            <select className="brutal-input sm:w-52" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="new-template-category">
              <option value="stamp">Stamp (blank canvas)</option>
              <option value="face_stamp">Face Stamp (photo → sketch + text)</option>
            </select>
            <div className="flex gap-2">
              <button className="brutal-btn bg-green-200 px-4 py-2 rounded-md" data-testid="new-template-create">Create</button>
              <button type="button" onClick={() => setCreating(false)} className="brutal-btn bg-white px-4 py-2 rounded-md">Cancel</button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="brutal-card p-6">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="brutal-card p-10 text-center">
            <div className="font-display text-4xl text-stone-700 mb-2">nothing here yet</div>
            <p className="text-stone-600 font-body mb-4">Create your first product template to get started.</p>
            <button onClick={() => setCreating(true)} className="brutal-btn bg-yellow-300 px-5 py-3 rounded-md inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New template
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((t) => (
              <div key={t.id} className="brutal-card p-4 flex flex-col" data-testid={`template-card-${t.id}`}>
                <div className="aspect-square bg-stone-100 border-2 border-stone-900 rounded-md overflow-hidden mb-3">
                  {t.base_image_url ? (
                    <img src={t.base_image_url} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{background: t.bg_color || "#fff"}}>
                      <span className="font-display text-2xl text-stone-500">No image</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-heading font-bold text-lg truncate">{t.name}</div>
                  {t.published ? (
                    <span className="text-xs font-bold uppercase bg-green-200 border-2 border-stone-900 rounded px-2 py-0.5 flex items-center gap-1"><Globe className="w-3 h-3"/>Live</span>
                  ) : (
                    <span className="text-xs font-bold uppercase bg-stone-200 border-2 border-stone-900 rounded px-2 py-0.5 flex items-center gap-1"><EyeOff className="w-3 h-3"/>Draft</span>
                  )}
                </div>
                <div className="text-xs text-stone-600 mb-1 flex items-center gap-2">
                  <span className={`font-bold uppercase text-[10px] tracking-widest border-2 border-stone-900 rounded px-1.5 py-0.5 ${t.category === "face_stamp" ? "bg-fuchsia-200" : "bg-white"}`} data-testid={`category-badge-${t.id}`}>
                    {t.category === "face_stamp" ? "Face Stamp" : "Stamp"}
                  </span>
                  <span>{(t.views?.reduce((a, v) => a + (v.zones?.length || 0), 0)) || (t.zones?.length || 0)} layers · {t.views?.length || 1} view{(t.views?.length || 1) > 1 ? 's' : ''}</span>
                </div>
                <div className="mb-3"/>
                <div className="mt-auto grid grid-cols-2 gap-2">
                  <Link to={`/templates/${t.id}/edit`} className="brutal-btn bg-yellow-300 px-3 py-2 rounded-md flex items-center justify-center gap-1 text-sm" data-testid={`edit-${t.id}`}>
                    <Edit3 className="w-4 h-4"/> Edit
                  </Link>
                  <button onClick={() => copyLink(t)} disabled={!t.published} className="brutal-btn bg-white px-3 py-2 rounded-md flex items-center justify-center gap-1 text-sm" data-testid={`link-${t.id}`}>
                    <Copy className="w-4 h-4"/> Link
                  </button>
                  <button onClick={() => copyEmbed(t)} disabled={!t.published} className="brutal-btn bg-white px-3 py-2 rounded-md flex items-center justify-center gap-1 text-sm" data-testid={`embed-${t.id}`}>
                    <Copy className="w-4 h-4"/> Button
                  </button>
                  <button onClick={() => copyIframe(t)} disabled={!t.published} className="brutal-btn bg-white px-3 py-2 rounded-md flex items-center justify-center gap-1 text-sm">
                    <Copy className="w-4 h-4"/> iframe
                  </button>
                  <button onClick={() => remove(t.id)} className="brutal-btn bg-red-200 px-3 py-2 rounded-md flex items-center justify-center gap-1 text-sm col-span-2" data-testid={`delete-${t.id}`}>
                    <Trash2 className="w-4 h-4"/> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
